import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, TextField, Typography,
} from '@mui/material'
import { RootState } from '../../store'
import { Bill } from '../../types'
import { getCompanyUom } from '../../utils/companyUom'
import { formatCurrency } from '../../utils/formatters'
import { useCreateSaleReturn, useQuoteSaleReturn, useSaleReturnEligibility } from '../../hooks/api/useSaleReturns'
import { ReturnDraft, ReturnEligibilityLine, ReturnQuote } from '../../services/saleReturns/saleReturnService'

interface Props {
  open: boolean
  onClose: () => void
  bill: Bill
}

interface LineEntry {
  units: string
  loose: string
  pieces: string
}

const emptyLine = (): LineEntry => ({ units: '', loose: '', pieces: '' })
const packingFor = (line: ReturnEligibilityLine) => line.sold_ctn > 0 ? line.sold_pcs / line.sold_ctn : 0
const canSplitUnits = (line: ReturnEligibilityLine) => {
  const packing = packingFor(line)
  return Number.isSafeInteger(packing) && packing > 0
}

const SaleReturnModal = ({ open, onClose, bill }: Props) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const [lines, setLines] = useState<Record<string, LineEntry>>({})
  const [cash, setCash] = useState('')
  const [reason, setReason] = useState('')
  const [returnDate, setReturnDate] = useState('')
  const [inputError, setInputError] = useState('')
  const [quote, setQuote] = useState<ReturnQuote | null>(null)
  const [quotedDraft, setQuotedDraft] = useState('')
  const requestKey = useRef(crypto.randomUUID())
  const submittedDraft = useRef('')
  const eligibility = useSaleReturnEligibility(bill._id, open)
  const availableLines = eligibility.data?.items.filter((line) => line.available_pcs > 0) ?? []
  const quoteReturn = useQuoteSaleReturn()
  const createReturn = useCreateSaleReturn()

  const resetQuote = () => {
    setQuote(null)
    setQuotedDraft('')
    setInputError('')
  }

  const updateLine = (id: string, update: Partial<LineEntry>) => {
    setLines((current) => ({ ...current, [id]: { ...(current[id] || emptyLine()), ...update } }))
    resetQuote()
  }

  const buildDraft = (): ReturnDraft => {
    const selected = availableLines.flatMap((line) => {
      const entry = lines[line.bill_item_id] || emptyLine()
      const packing = packingFor(line)
      let quantity: number
      if (canSplitUnits(line)) {
        const units = entry.units === '' ? 0 : Number(entry.units)
        const loose = entry.loose === '' ? 0 : Number(entry.loose)
        if (!Number.isFinite(units) || units < 0 || (!uom.allows_decimal && !Number.isInteger(units))
          || !Number.isSafeInteger(loose) || loose < 0 || loose >= packing) {
          throw new Error(`Enter valid ${uom.plural.toLowerCase()} and loose pieces`)
        }
        quantity = units * packing + loose
      } else {
        quantity = entry.pieces === '' ? 0 : Number(entry.pieces)
      }
      if (Math.abs(quantity - Math.round(quantity)) < 0.000001) quantity = Math.round(quantity)
      if (!Number.isFinite(quantity) || quantity < 0 || !Number.isSafeInteger(quantity)) {
        throw new Error('Returned quantity must be whole pieces; this invoice has a fractional piece quantity')
      }
      if (quantity > line.available_pcs) throw new Error('Returned quantity exceeds the available invoice quantity')
      return quantity > 0 ? [{
        bill_item_id: line.bill_item_id, quantity_pcs: quantity,
      }] : []
    })
    if (selected.length === 0) throw new Error('Select at least one invoice item to return')
    const cashAmount = cash === '' ? 0 : Number(cash)
    if (!Number.isFinite(cashAmount) || cashAmount < 0
      || (cash !== '' && !/^\d+(\.\d{1,2})?$/.test(cash))) {
      throw new Error('Cash amount must be zero or a valid amount with at most two decimals')
    }
    return { bill_id: bill._id, items: selected, cash_amount: cashAmount }
  }

  const handleQuote = async () => {
    try {
      const draft = buildDraft()
      setInputError('')
      const response = await quoteReturn.mutateAsync(draft)
      setQuote(response)
      setQuotedDraft(JSON.stringify(draft))
    } catch (error) {
      if (error instanceof Error) setInputError(error.message)
    }
  }

  const handleSave = () => {
    try {
      const draft = buildDraft()
      if (!quote || JSON.stringify(draft) !== quotedDraft) {
        setInputError('Calculate the return credit again before saving')
        return
      }
      const submission = JSON.stringify({ ...draft, reason: reason.trim(), returnDate })
      if (submittedDraft.current && submittedDraft.current !== submission) {
        requestKey.current = crypto.randomUUID()
      }
      submittedDraft.current = submission
      createReturn.mutate({
        ...draft, request_key: requestKey.current, reason: reason.trim(),
        ...(returnDate && { return_date: returnDate }),
      }, { onSuccess: onClose })
    } catch (error) {
      if (error instanceof Error) setInputError(error.message)
    }
  }

  const busy = quoteReturn.isPending || createReturn.isPending
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Record Sale Return</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {bill.customer_name} · Bill #{bill._id}. Cash collected and returned goods are recorded separately.
        </Typography>
        {eligibility.isLoading && <Box py={3} textAlign="center"><CircularProgress /></Box>}
        {eligibility.isError && <Alert severity="error">Could not load return eligibility for this bill.</Alert>}
        {eligibility.data && <>
          <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
            <Typography variant="body2">Invoice: Rs {formatCurrency(bill.summary.net_payable)}</Typography>
            <Typography variant="body2">Cash collected: Rs {formatCurrency(eligibility.data.paid_amount)}</Typography>
            <Typography variant="body2">Earlier returns: Rs {formatCurrency(eligibility.data.returned_amount)}</Typography>
            <Typography variant="body2" fontWeight={700}>Due: Rs {formatCurrency(eligibility.data.remaining_balance)}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {availableLines.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>
            No invoice items remain available for return.
          </Alert>}
          <Box display="flex" flexDirection="column" gap={1.5}>
            {availableLines.map((line) => {
              const entry = lines[line.bill_item_id] || emptyLine()
              const item = bill.sku_items.find((row) => row._id === line.bill_item_id)
              const name = item && typeof item.sku_id === 'object' ? item.sku_id.name : 'Invoice item'
              const split = canSplitUnits(line)
              return <Paper key={line.bill_item_id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography fontWeight={600}>{name}</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Sold {line.sold_ctn} {uom.code} ({line.sold_pcs} pieces) · Returned {line.returned_pcs} pieces · Available {line.available_pcs} pieces
                </Typography>
                <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                  {split ? <>
                    <TextField size="small" type="number" label={uom.plural} value={entry.units}
                      onChange={(event) => updateLine(line.bill_item_id, { units: event.target.value })}
                      inputProps={{ min: 0, step: uom.allows_decimal ? 0.001 : 1 }} sx={{ width: 140 }} />
                    <TextField size="small" type="number" label="Loose pieces" value={entry.loose}
                      onChange={(event) => updateLine(line.bill_item_id, { loose: event.target.value })}
                      inputProps={{ min: 0, max: packingFor(line) - 1, step: 1 }} sx={{ width: 140 }} />
                  </> : <TextField size="small" type="number" label="Pieces" value={entry.pieces}
                    onChange={(event) => updateLine(line.bill_item_id, { pieces: event.target.value })}
                    inputProps={{ min: 0, step: 1 }} sx={{ width: 140 }} />}
                </Box>
                {!Number.isInteger(line.available_pcs) && <Typography variant="caption" color="warning.main">
                  The backend currently accepts whole pieces only; a fractional remainder cannot be returned.
                </Typography>}
              </Paper>
            })}
          </Box>
          <Box display="flex" gap={2} mt={2} flexWrap="wrap">
            <TextField size="small" type="number" label="Cash received (optional)" value={cash}
              onChange={(event) => { setCash(event.target.value); resetQuote() }}
              inputProps={{ min: 0, step: 0.01 }} sx={{ minWidth: 220 }} />
            <TextField size="small" type="date" label="Return date" value={returnDate}
              onChange={(event) => setReturnDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
          <TextField fullWidth multiline minRows={2} label="Reason (optional)" value={reason}
            onChange={(event) => setReason(event.target.value)} sx={{ mt: 2 }} />
          {inputError && <Alert severity="error" sx={{ mt: 2 }}>{inputError}</Alert>}
          {quote && <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography fontWeight={700} mb={1}>Calculated from original invoice</Typography>
            <Typography>Return credit: Rs {formatCurrency(quote.credit_amount)}</Typography>
            <Typography>Cash received: Rs {formatCurrency(quote.cash_amount)}</Typography>
            <Typography fontWeight={700}>Remaining balance: Rs {formatCurrency(quote.remaining_balance)}</Typography>
          </Paper>}
        </>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleQuote} disabled={availableLines.length === 0 || busy}>Calculate credit</Button>
        <Button variant="contained" onClick={handleSave} disabled={!quote || availableLines.length === 0 || busy}>
          {createReturn.isPending ? 'Saving...' : 'Record Return'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaleReturnModal
